import React, { memo, useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard } from '@/components/shared/redesign';
import { GlassStatusBadge, GlassStatusVariant } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { CorkBoardReport, ReportStatus } from '@/types/residents/corkBoard';

interface ReportReviewListItemProps {
  report: CorkBoardReport;
  isMutating: boolean;
  onView: (report: CorkBoardReport) => void;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
}

const STATUS_VARIANT_MAP: Record<ReportStatus, GlassStatusVariant> = {
  pending: 'warning',
  reviewed: 'info',
  resolved: 'success',
  dismissed: 'neutral',
};

export const ReportReviewListItem: React.FC<ReportReviewListItemProps> = memo(
  ({ report, isMutating, onView, onRemove, onDismiss, onWarn }) => {
    const { redesign, semantic } = useAdminColors();
    const isActionable = report.status === 'pending' || report.status === 'reviewed';

    const formattedDate = useMemo(() => {
      const date = new Date(report.createdAt);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }, [report.createdAt]);

    const statusVariant = STATUS_VARIANT_MAP[report.status] ?? 'warning';
    const statusLabel = report.status.charAt(0).toUpperCase() + report.status.slice(1);
    const reasonLabel = report.reason.charAt(0).toUpperCase() + report.reason.slice(1);
    const typeLabel = report.contentType === 'thread' ? 'Thread' : 'Reply';
    const typeVariant: GlassStatusVariant = report.contentType === 'thread' ? 'info' : 'neutral';

    return (
      <GlassCard padding={0} style={styles.cardRoot}>
        <View style={styles.container}>
          <View style={styles.topRow}>
            <GlassStatusBadge label={typeLabel} variant={typeVariant} filled />
            <Text
              style={[styles.contentPreview, { color: redesign.text.primary }]}
              numberOfLines={1}
            >
              {report.contentPreview || '(no preview)'}
            </Text>
            <GlassStatusBadge label={statusLabel} variant={statusVariant} />
          </View>

          <View style={styles.middleRow}>
            <Text style={[styles.metaText, { color: redesign.text.secondary }]}>
              Reported by {report.reporter.name}
            </Text>
            <Text style={[styles.metaSeparator, { color: redesign.text.subtle }]}>·</Text>
            <GlassStatusBadge label={reasonLabel} variant="neutral" />
            <Text style={[styles.metaSeparator, { color: redesign.text.subtle }]}>·</Text>
            <Text style={[styles.metaText, { color: redesign.text.secondary }]}>{formattedDate}</Text>
          </View>

          <View style={styles.bottomRow}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onView(report)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={[styles.actionText, { color: semantic.text.link }]}>View</Text>
            </TouchableOpacity>

            {isActionable && (
              <>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => onRemove(report)}
                  disabled={isMutating}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text
                    style={[
                      styles.actionText,
                      { color: semantic.status.error },
                      isMutating && styles.actionTextDisabled,
                    ]}
                  >
                    Remove
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => onDismiss(report)}
                  disabled={isMutating}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text
                    style={[
                      styles.actionText,
                      { color: semantic.text.link },
                      isMutating && styles.actionTextDisabled,
                    ]}
                  >
                    Dismiss
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => onWarn(report)}
                  disabled={isMutating}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text
                    style={[
                      styles.actionText,
                      { color: semantic.status.warning },
                      isMutating && styles.actionTextDisabled,
                    ]}
                  >
                    Warn
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </GlassCard>
    );
  },
);

ReportReviewListItem.displayName = 'ReportReviewListItem';

const styles = StyleSheet.create({
  cardRoot: {
    marginBottom: 8,
  },
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 8,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  contentPreview: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  middleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  metaText: {
    fontSize: 12,
  },
  metaSeparator: {
    fontSize: 12,
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
  },
  actionTextDisabled: {
    opacity: 0.4,
  },
});
